import React, { useState, useEffect } from "react";
import {
	Box,
	Grid,
	TextField,
	Typography,
	Button,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MapModal from "../../../components/MapModal";
import DocumentModal from "../../../components/DocumentModal";

function AbaEmpresa({ dados, setDados, onAddDocumentoClick }) {
	const [documentos, setDocumentos] = useState(dados.documentos || []);
	const [openMapModal, setOpenMapModal] = useState(false);
	const [openDocumentModal, setOpenDocumentModal] = useState(false);
	const [editingDocument, setEditingDocument] = useState(null);

	// Expor a função handleAddDocumento para o componente pai
	useEffect(() => {
		if (onAddDocumentoClick) {
			onAddDocumentoClick(() => handleAddDocumento);
		}
	}, [onAddDocumentoClick]);

	// Tentar obter localização automaticamente se não tiver coordenadas
	useEffect(() => {
		if (!dados.latitude && !dados.longitude && !dados.cep) {
			// Aguardar um pouco antes de solicitar localização
			const timer = setTimeout(() => {
				obterLocalizacaoAtual();
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const formatCpfCnpj = (value) => {
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			// Formato CPF: 000.000.000-00
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			// Formato CNPJ: 00.000.000/0000-00
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	};

	const handleChange = (field, value) => {
		setDados((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleCpfCnpjChange = (value) => {
		const formatted = formatCpfCnpj(value);
		handleChange("cpfCnpj", formatted);
	};

	const handleCepChange = async (cep) => {
		handleChange("cep", cep);

		// Remove caracteres não numéricos
		const cepLimpo = cep.replace(/\D/g, "");

		if (cepLimpo.length === 8) {
			try {
				const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
				const data = await response.json();

				if (!data.erro) {
					handleChange("uf", data.uf);
					handleChange("cidade", data.localidade);
					handleChange("bairro", data.bairro);
					handleChange("logradouro", data.logradouro);

					// Se encontrou o endereço, buscar coordenadas pela localidade
					if (data.localidade && data.uf) {
						buscarCoordenadasPorCidade(data.localidade, data.uf);
					}
				} else {
					alert("CEP não encontrado");
				}
			} catch (error) {
				console.error("Erro ao buscar CEP:", error);
				alert("Erro ao buscar CEP. Verifique sua conexão.");
			}
		}
	};

	const buscarCoordenadasPorCidade = async (cidade, uf) => {
		try {
			// Usando a API do Nominatim (OpenStreetMap) para geocodificação
			const response = await fetch(
				`https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(cidade)}&state=${encodeURIComponent(uf)}&country=Brazil&format=json&limit=1`
			);
			const data = await response.json();

			if (data && data.length > 0) {
				handleChange("latitude", parseFloat(data[0].lat));
				handleChange("longitude", parseFloat(data[0].lon));
			}
		} catch (error) {
			console.error("Erro ao buscar coordenadas:", error);
		}
	};

	const handleAddDocumento = () => {
		setEditingDocument(null);
		setOpenDocumentModal(true);
	};

	const handleEditDocumento = (doc) => {
		setEditingDocument(doc);
		setOpenDocumentModal(true);
	};

	const handleRemoveDocumento = (id) => {
		const novosDocumentos = documentos.filter((doc) => doc.id !== id);
		setDocumentos(novosDocumentos);
		handleChange("documentos", novosDocumentos);
	};

	const handleSaveDocumento = (documentData) => {
		let novosDocumentos;
		if (editingDocument) {
			// Editando documento existente
			novosDocumentos = documentos.map((doc) =>
				doc.id === editingDocument.id ? documentData : doc
			);
		} else {
			// Adicionando novo documento
			novosDocumentos = [...documentos, documentData];
		}
		setDocumentos(novosDocumentos);
		handleChange("documentos", novosDocumentos);
	};

	const handleOpenMap = () => {
		// Se não tem coordenadas, tentar obter localização atual
		if (!dados.latitude && !dados.longitude) {
			obterLocalizacaoAtual();
		}
		setOpenMapModal(true);
	};

	const obterLocalizacaoAtual = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					handleChange("latitude", position.coords.latitude);
					handleChange("longitude", position.coords.longitude);
				},
				(error) => {
					console.error("Erro ao obter localização:", error);
					// Usar coordenadas padrão (Fortaleza, CE)
					if (!dados.latitude && !dados.longitude) {
						handleChange("latitude", -3.71839);
						handleChange("longitude", -38.5434);
					}
				}
			);
		}
	};

	const handleSaveCoordinates = (latitude, longitude) => {
		handleChange("latitude", latitude);
		handleChange("longitude", longitude);
	};

	const handleVisualizarDocumento = (doc) => {
		if (doc.arquivo) {
			// Se o arquivo é um objeto File (do upload)
			if (doc.arquivo instanceof File) {
				const url = URL.createObjectURL(doc.arquivo);
				window.open(url, "_blank");
			} else if (typeof doc.arquivo === "string") {
				// Se já é uma URL (quando vier do backend)
				window.open(doc.arquivo, "_blank");
			}
		} else {
			alert("Documento não possui arquivo anexado");
		}
	};

	return (
		<Box>
			<Grid container spacing={3}>
				{/* Dados Principais - Primeira linha: 2 campos */}
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						label="Nome / Razão Social"
						value={dados.nome || ""}
						onChange={(e) => handleChange("nome", e.target.value)}
						required
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						label="Apelido / Nome Fantasia"
						value={dados.nomeFantasia || ""}
						onChange={(e) => handleChange("nomeFantasia", e.target.value)}
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
					/>
				</Grid>

				{/* Segunda linha: 4 campos */}
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						fullWidth
						label="CPF / CNPJ"
						value={dados.cpfCnpj || ""}
						onChange={(e) => handleCpfCnpjChange(e.target.value)}
						required
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{
							style: { fontSize: "15px" },
							maxLength: 18
						}}
						placeholder="000.000.000-00"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						fullWidth
						label="IE / RG"
						value={dados.ieRg || ""}
						onChange={(e) => handleChange("ieRg", e.target.value)}
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						fullWidth
						label="E-mail"
						type="email"
						value={dados.email || ""}
						onChange={(e) => handleChange("email", e.target.value)}
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						fullWidth
						label="Telefone"
						value={dados.telefone || ""}
						onChange={(e) => handleChange("telefone", e.target.value)}
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
					/>
				</Grid>

				{/* Endereço - Separação visual clara */}
				<Grid size={{ xs: 12 }}>
					<Typography
						variant="subtitle1"
						sx={{ color: "#1a9e8f", fontWeight: 600, mt: 1, mb: 2, fontSize: "16px" }}
					>
						Endereço
					</Typography>

					{/* Bloco de Endereço */}
					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="CEP"
								value={dados.cep || ""}
								onChange={(e) => handleCepChange(e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 1 }}>
							<TextField
								fullWidth
								label="UF"
								value={dados.uf || ""}
								onChange={(e) => handleChange("uf", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 5 }}>
							<TextField
								fullWidth
								label="Cidade"
								value={dados.cidade || ""}
								onChange={(e) => handleChange("cidade", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Bairro"
								value={dados.bairro || ""}
								onChange={(e) => handleChange("bairro", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								label="Logradouro"
								value={dados.logradouro || ""}
								onChange={(e) => handleChange("logradouro", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>

						{/* Coordenadas */}
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Latitude"
								type="number"
								value={dados.latitude || ""}
								onChange={(e) => handleChange("latitude", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Longitude"
								type="number"
								value={dados.longitude || ""}
								onChange={(e) => handleChange("longitude", e.target.value)}
								InputLabelProps={{ style: { fontSize: "15px" } }}
								inputProps={{ style: { fontSize: "15px" } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 4 }}>
							<Button
								fullWidth
								variant="outlined"
								startIcon={<MapIcon />}
								onClick={handleOpenMap}
								sx={{
									height: "48px",
									textTransform: "none",
									borderColor: "#d0d0d0",
									color: "#666",
									fontSize: "15px",
									"&:hover": {
										borderColor: "#1a9e8f",
										color: "#1a9e8f",
										bgcolor: "rgba(23, 162, 184, 0.04)",
									},
								}}
							>
								Selecionar no Mapa
							</Button>
						</Grid>
					</Grid>
				</Grid>

				{/* Documentos - Separação visual clara */}
				<Grid size={{ xs: 12 }}>
					<Typography
						variant="subtitle1"
						sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2, mt: 1 }}
					>
						Documentos
					</Typography>

					{/* Tabela de Documentos */}
					{documentos.length > 0 ? (
						<TableContainer sx={{ px: 0 }}>
							<Table sx={{ tableLayout: "fixed" }}>
								<TableHead>
									<TableRow>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pl: 0, pr: 2 }}>
											Documento
										</TableCell>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", px: 2, width: "20%" }}>
											Data de expedição
										</TableCell>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", px: 2, width: "20%" }}>
											Data de validade
										</TableCell>
										<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pl: 2, pr: 0, width: "150px" }} align="center">
											Ações
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{documentos.map((doc) => (
										<TableRow key={doc.id} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
											<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pl: 0, pr: 2 }}>
												{doc.nome || "-"}
											</TableCell>
											<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", px: 2 }}>
												{doc.dataExpedicao ? new Date(doc.dataExpedicao).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "-"}
											</TableCell>
											<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", px: 2 }}>
												{doc.dataValidade ? new Date(doc.dataValidade).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "-"}
											</TableCell>
											<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pl: 2, pr: 0 }} align="center">
												<IconButton
													size="small"
													sx={{ color: "#6C757D" }}
													title="Visualizar documento"
													onClick={() => handleVisualizarDocumento(doc)}
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													sx={{ color: "#1a9e8f", ml: 0.5 }}
													onClick={() => handleEditDocumento(doc)}
													title="Editar documento"
												>
													<EditIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													onClick={() => handleRemoveDocumento(doc.id)}
													sx={{ color: "#ff4d4d", ml: 0.5 }}
													title="Excluir documento"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Box
							sx={{
								textAlign: "center",
								py: 4,
								px: 8,
								color: "#999",
								border: "2px dashed #e0e0e0",
								borderRadius: 1,
								width: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "120px",
							}}
						>
							<Typography variant="body1" sx={{ fontSize: "16px", fontWeight: 500 }}>
								Nenhum documento adicionado
							</Typography>
						</Box>
					)}
				</Grid>
			</Grid>

			{/* Modais */}
			<MapModal
				open={openMapModal}
				onClose={() => setOpenMapModal(false)}
				initialLat={dados.latitude}
				initialLon={dados.longitude}
				onSave={handleSaveCoordinates}
			/>

			<DocumentModal
				open={openDocumentModal}
				onClose={() => {
					setOpenDocumentModal(false);
					setEditingDocument(null);
				}}
				initialData={editingDocument}
				onSave={handleSaveDocumento}
			/>
		</Box>
	);
}

export default AbaEmpresa;
